import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import { useRepository } from '../../data/RepositoryProvider';
import { MutationError } from '../partners/PartnerFormDialog';
import { toSafePartnerGameError } from './partnerGameError';
import { defaultGameListQuery, gamesRepositoryToken } from './repository';
import type { GameSummary, PartnerGame } from './types';
import { normalizeMasterCode } from './types';

export type PartnerGameDialogMode = 'create' | 'edit' | 'status';

interface Values {
  readonly gameId: string;
  readonly maximumPlayAmount: string;
  readonly status: PartnerGame['status'];
  readonly reason: string;
}

export function PartnerGameDialog({
  mode,
  onClose,
  onSaved,
  partnerGame,
  partnerId,
}: {
  readonly mode: PartnerGameDialogMode;
  readonly onClose: () => void;
  readonly onSaved: (partnerGame: PartnerGame) => void;
  readonly partnerGame?: PartnerGame | undefined;
  readonly partnerId: string;
}) {
  const repository = useRepository(gamesRepositoryToken);
  const { t } = useTranslation();
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const [gameCode, setGameCode] = useState('');
  const [appliedGameCode, setAppliedGameCode] = useState('');
  const [games, setGames] = useState<readonly GameSummary[]>([]);
  const [gameTotal, setGameTotal] = useState(0);
  const [gamesLoading, setGamesLoading] = useState(mode === 'create');
  const [gamesError, setGamesError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(
    null,
  );
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<Values>({ defaultValues: defaults(mode, partnerGame) });
  const proposedStatus = watch('status');
  const proposedAmount = watch('maximumPlayAmount');

  useEffect(() => {
    reset(defaults(mode, partnerGame));
    setError(null);
    submittingRef.current = false;
    setSubmitting(false);
  }, [mode, partnerGame, reset]);

  useEffect(() => {
    if (mode !== 'create') return;
    const controller = new AbortController();
    setGamesLoading(true);
    setGamesError(null);
    void repository
      .listGames(
        {
          ...defaultGameListQuery,
          pageSize: 25,
          sort: 'code',
          order: 'asc',
          filters: {
            gameTypeId: '',
            code: normalizeMasterCode(appliedGameCode),
            name: '',
            status: 'ACTIVE',
          },
        },
        controller.signal,
      )
      .then((page) => {
        if (controller.signal.aborted) return;
        setGames(page.items);
        setGameTotal(page.total);
      })
      .catch((loadError: unknown) => {
        if (!controller.signal.aborted)
          setGamesError(toSafePartnerGameError(loadError, t, t('games.partner.errors.loadGames')));
      })
      .finally(() => {
        if (!controller.signal.aborted) setGamesLoading(false);
      });
    return () => controller.abort();
  }, [appliedGameCode, mode, repository, t]);

  const submit = handleSubmit(async (values) => {
    if (submittingRef.current) return;
    if (mode !== 'create' && partnerGame === undefined) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const result =
        mode === 'create'
          ? await repository.createPartnerGame(partnerId, {
              gameId: values.gameId,
              maximumPlayAmount: values.maximumPlayAmount.trim(),
              reason: values.reason.trim(),
            })
          : mode === 'edit'
            ? await repository.updatePartnerGame(partnerId, partnerGame!.game.id, {
                maximumPlayAmount: values.maximumPlayAmount.trim(),
                reason: values.reason.trim(),
              })
            : await repository.changePartnerGameStatus(partnerId, partnerGame!.game.id, {
                status: values.status,
                reason: values.reason.trim(),
              });
      onSaved(result.data);
      onClose();
    } catch (submitError) {
      setError(toSafePartnerGameError(submitError, t, t('games.partner.errors.mutation')));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  });

  const title = t(`games.partner.dialog.${mode}Title`);
  const descriptionId = `partner-game-${mode}-description`;
  const formId = `partner-game-${mode}-form`;

  return (
    <Dialog
      aria-describedby={descriptionId}
      fullWidth
      maxWidth="sm"
      onClose={() => !submittingRef.current && onClose()}
      open
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id={descriptionId} sx={{ mb: 2 }}>
          {t(`games.partner.dialog.${mode}Description`)}
        </DialogContentText>
        <Stack component="form" id={formId} noValidate onSubmit={submit} spacing={2}>
          {error === null ? null : <MutationError error={error} />}
          {mode === 'create' ? (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  disabled={submitting}
                  label={t('games.partner.fields.gameSearch')}
                  onChange={(event) => setGameCode(event.target.value)}
                  size="small"
                  value={gameCode}
                />
                <Button
                  disabled={submitting}
                  onClick={() => setAppliedGameCode(gameCode)}
                  variant="outlined"
                >
                  {t('games.partner.actions.searchGames')}
                </Button>
              </Stack>
              {gamesError === null ? null : <MutationError error={gamesError} />}
              <TextField
                defaultValue=""
                disabled={submitting || gamesLoading || gamesError !== null}
                error={errors.gameId !== undefined}
                helperText={
                  errors.gameId?.message ??
                  t('games.partner.dialog.gameResults', { shown: games.length, total: gameTotal })
                }
                label={t('games.partner.fields.game')}
                select
                {...register('gameId', { required: t('games.partner.validation.gameRequired') })}
              >
                {games.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.code} · {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </>
          ) : (
            <Alert severity="info">
              <Typography>{partnerGame!.game.code}</Typography>
              <Typography variant="body2">
                {t('games.partner.dialog.lockedCurrency')}: {partnerGame!.currencyCode}
              </Typography>
            </Alert>
          )}
          {mode === 'status' ? (
            <TextField
              defaultValue={partnerGame?.status ?? 'SUSPENDED'}
              disabled={submitting}
              label={t('games.fields.proposedStatus')}
              select
              {...register('status')}
            >
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
            </TextField>
          ) : (
            <TextField
              disabled={submitting}
              error={errors.maximumPlayAmount !== undefined}
              helperText={errors.maximumPlayAmount?.message}
              label={t('games.partner.fields.maximumPlayAmount')}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              {...register('maximumPlayAmount', {
                required: t('games.partner.validation.amountRequired'),
                pattern: {
                  value: /^(?!0(?:\.0{1,6})?$)(?:0|[1-9][0-9]{0,13})(?:\.[0-9]{1,6})?$/u,
                  message: t('games.partner.validation.amountInvalid'),
                },
              })}
            />
          )}
          <Alert severity="warning">
            {mode === 'status'
              ? t('games.partner.dialog.statusImpact', {
                  current: partnerGame?.status,
                  next: proposedStatus,
                })
              : t('games.partner.dialog.amountImpact', {
                  current: partnerGame?.maximumPlayAmount ?? t('common.notAvailable'),
                  next: proposedAmount,
                })}
          </Alert>
          <TextField
            disabled={submitting}
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('games.fields.reason')}
            minRows={2}
            multiline
            required
            {...register('reason', {
              maxLength: { value: 2000, message: t('games.validation.reasonMaximum') },
              validate: (value) => value.trim().length > 0 || t('games.validation.reasonRequired'),
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={submitting} onClick={onClose}>
          {t('actions.cancel')}
        </Button>
        <Button form={formId} loading={submitting} type="submit" variant="contained">
          {mode === 'create'
            ? t('games.partner.actions.configure')
            : mode === 'edit'
              ? t('games.partner.actions.save')
              : t('actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function defaults(mode: PartnerGameDialogMode, partnerGame: PartnerGame | undefined): Values {
  return {
    gameId: mode === 'create' ? '' : (partnerGame?.game.id ?? ''),
    maximumPlayAmount: mode === 'status' ? '' : (partnerGame?.maximumPlayAmount ?? ''),
    status:
      mode === 'status' && partnerGame?.status === 'SUSPENDED'
        ? 'ACTIVE'
        : (partnerGame?.status ?? 'SUSPENDED'),
    reason: '',
  };
}
